import {
  closestPointOnSegment,
  distancePointToSegment,
  insetPolygon,
  isSimplePolygon,
  pointInOrOnPolygon,
  polygonArea,
  type Vec2,
} from '@indusketch/geometry';
import { hallInterior } from './derived/hallSections.js';
import {
  hostHall,
  levelElevations,
  officeSlabOutline,
  stairGeometry,
  stairLayout,
  stairToWorld,
  wallBase,
} from './office.js';
import { wallFrame } from './derived/walls.js';
import { cutAtGates, mezzanineGateSpans } from './railingGates.js';
import type { Building, Mezzanine, ProjectDocument, Stair } from './types.js';

export const MEZZANINE_DEFAULTS = { elevation: 3.5, slabThickness: 0.3, color: '#8a9096' } as const;
/** Clear height that must stay between the walking surface and the eaves. */
export const MEZZANINE_HEADROOM = 2;
export const MEZZANINE_COLUMN = 0.2;
const COLUMN_SPACING = 6;
/** How close an edge has to run along the inner wall face to count as "on the wall". */
const WALL_TOLERANCE = 0.35;

export type MezzanineProblem = 'mezzanineShape' | 'mezzanineOutside' | 'mezzanineHeight';

const edgeOf = (poly: readonly Vec2[], i: number) => ({
  a: poly[i] as Vec2,
  b: poly[(i + 1) % poly.length] as Vec2,
});

const mid = (a: Vec2, b: Vec2): Vec2 => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/** Why a mezzanine with this outline and height does not fit into the hall, or null. */
export function mezzanineProblem(
  hall: Building,
  polygon: readonly Vec2[],
  elevation: number,
  slabThickness: number,
): MezzanineProblem | null {
  if (polygon.length < 3 || !isSimplePolygon(polygon) || polygonArea(polygon) < 1)
    return 'mezzanineShape';
  const inner = hallInterior(hall);
  const probes = polygon.flatMap((p, i) => [p, mid(p, edgeOf(polygon, i).b)]);
  if (!probes.every((p) => pointInOrOnPolygon(p, inner, 0.05))) return 'mezzanineOutside';
  if (elevation < slabThickness || elevation > hall.eaveHeight - MEZZANINE_HEADROOM + 1e-9)
    return 'mezzanineHeight';
  return null;
}

export const mezzanineArea = (m: Pick<Mezzanine, 'polygon'>): number => polygonArea(m.polygon);

/** Corners this far beyond the inner wall face (in the wall or just outside) are pulled in. */
const PULL_IN = 0.5;

/**
 * Outline moved onto the inner face of the hall walls where it reaches into them, e.g. corners
 * snapped to the outer corners of the hall. Corners further outside stay (and fail the check).
 */
export function fitIntoHall(hall: Building, polygon: readonly Vec2[]): Vec2[] {
  const inner = hallInterior(hall);
  const out: Vec2[] = [];
  for (const p of polygon) {
    let q = p;
    if (!pointInOrOnPolygon(p, inner, 1e-6)) {
      let best: { point: Vec2; d: number } | null = null;
      for (let i = 0; i < inner.length; i++) {
        const c = closestPointOnSegment(p, edgeOf(inner, i));
        const d = Math.hypot(c.x - p.x, c.y - p.y);
        if (!best || d < best.d) best = { point: c, d };
      }
      if (best && best.d <= hall.wallThickness + PULL_IN) q = best.point;
    }
    const last = out[out.length - 1];
    if (!last || Math.hypot(last.x - q.x, last.y - q.y) > 1e-6) out.push(q);
  }
  const first = out[0];
  const last = out[out.length - 1];
  if (out.length > 1 && first && last && Math.hypot(first.x - last.x, first.y - last.y) <= 1e-6)
    out.pop();
  return out;
}

export interface MezzanineEdge {
  readonly a: Vec2;
  readonly b: Vec2;
  /** Open towards the hall (needs a railing), as opposed to running along a hall wall. */
  readonly free: boolean;
}

export function mezzanineEdges(
  hall: Building | undefined,
  m: Pick<Mezzanine, 'polygon'>,
): MezzanineEdge[] {
  const inner = hall ? hallInterior(hall) : [];
  const onWall = (p: Vec2) =>
    inner.some((_, i) => distancePointToSegment(p, edgeOf(inner, i)) <= WALL_TOLERANCE);
  return m.polygon.map((_, i) => {
    const { a, b } = edgeOf(m.polygon, i);
    return { a, b, free: !(onWall(a) && onWall(b) && onWall(mid(a, b))) };
  });
}

type MezzanineDoc = Pick<ProjectDocument, 'buildings' | 'stairs' | 'mezzanines' | 'walls'>;
type DeckDoc = Pick<ProjectDocument, 'buildings' | 'stairs' | 'walls'> & {
  readonly mezzanines?: ProjectDocument['mezzanines'];
};

/**
 * Railing pieces along the free edges; where a stair arrives the railing leaves a gap of the
 * stair width.
 */
export function mezzanineRailings(doc: MezzanineDoc, m: Mezzanine): { a: Vec2; b: Vec2 }[] {
  if (!m.railing) return [];
  const stairs = Object.values(doc.stairs).filter((s) => s.buildingId === m.buildingId);
  const runs = deckRailings(doc, doc.buildings[m.buildingId], m.polygon, stairs, {
    elevation: m.elevation,
    off: m.railingOff,
    self: { mezzanineId: m.id },
  });
  // Handover gates close the railing themselves.
  return cutAtGates(runs, mezzanineGateSpans(m));
}

/**
 * Railing along the free edges of a walkable deck (mezzanine, usable office top) in `hall`,
 * with a gap where one of `stairs` arrives.
 */
export function deckRailings(
  doc: DeckDoc,
  hall: Building | undefined,
  polygon: readonly Vec2[],
  stairs: readonly Stair[],
  ctx: DeckContext,
): { a: Vec2; b: Vec2 }[] {
  const arrivals = stairs.map((s) => {
    const line = stairGeometry(doc, s).walkLine;
    const end = line[line.length - 1] ?? { x: 0, y: 0 };
    return { at: stairToWorld(s, end), half: s.width / 2 + 0.05 };
  });
  // Other walkable surfaces at about this height: one walks over, so no railing there.
  const neighbours = [
    ...Object.values(doc.mezzanines ?? {})
      .filter((m) => m.id !== ctx.self.mezzanineId)
      .map((m) => ({ polygon: m.polygon, elevation: m.elevation })),
    ...Object.values(doc.buildings).flatMap((b) => {
      const d = b.id === ctx.self.officeId ? null : officeDeck(doc, b);
      return d ? [{ polygon: d.polygon, elevation: d.elevation }] : [];
    }),
  ].filter((n) => Math.abs(n.elevation - ctx.elevation) <= DECK_STEP);
  // Walls standing on the deck along an edge (room walls, office walls) close it off already.
  const walls = Object.values(doc.walls).flatMap((w) => {
    if (w.ownerId !== null && w.ownerId === ctx.self.officeId) return [];
    const base = wallBase(doc, w);
    if (base > ctx.elevation + 0.3 || base + w.height < ctx.elevation + 1) return [];
    return [
      { seg: { a: w.start, b: w.end }, dir: wallFrame(w).dir, reach: w.thickness / 2 + 0.25 },
    ];
  });
  const out: { a: Vec2; b: Vec2 }[] = [];
  mezzanineEdges(hall, { polygon: [...polygon] }).forEach((e, index) => {
    if (!e.free || ctx.off.includes(index)) return;
    const len = Math.hypot(e.b.x - e.a.x, e.b.y - e.a.y);
    if (len < 1e-6) return;
    const dir = { x: (e.b.x - e.a.x) / len, y: (e.b.y - e.a.y) / len };
    // Outward normal of a counter-clockwise outline.
    const outward = { x: dir.y, y: -dir.x };
    const at = (t: number): Vec2 => ({ x: e.a.x + dir.x * t, y: e.a.y + dir.y * t });
    const blocked = (p: Vec2) => {
      const beyond = { x: p.x + outward.x * 0.25, y: p.y + outward.y * 0.25 };
      if (neighbours.some((n) => pointInOrOnPolygon(beyond, n.polygon, 0.1))) return true;
      return walls.some(
        (w) =>
          Math.abs(w.dir.x * dir.x + w.dir.y * dir.y) > 0.95 &&
          distancePointToSegment(p, w.seg) <= w.reach,
      );
    };
    const gaps: (readonly [number, number])[] = arrivals
      .filter((s) => distancePointToSegment(s.at, e) <= 0.6)
      .map((s) => {
        const t = (s.at.x - e.a.x) * dir.x + (s.at.y - e.a.y) * dir.y;
        return [t - s.half, t + s.half] as const;
      });
    // Blocked stretches, sampled every 10 cm along the edge.
    const steps = Math.max(1, Math.ceil(len / 0.1));
    for (let k = 0; k < steps; k++) {
      const t0 = (len * k) / steps;
      const t1 = (len * (k + 1)) / steps;
      if (blocked(at((t0 + t1) / 2))) gaps.push([t0, t1]);
    }
    gaps.sort((p, q) => p[0] - q[0]);
    let from = 0;
    for (const [g0, g1] of gaps) {
      if (g0 - from > 0.05) out.push({ a: at(from), b: at(Math.min(g0, len)) });
      from = Math.max(from, g1);
    }
    if (len - from > 0.05) out.push({ a: at(Math.max(from, 0)), b: e.b });
  });
  return out;
}

/** Height difference up to which two neighbouring decks count as one walkable floor. */
const DECK_STEP = 0.5;

export interface DeckContext {
  /** Walking height of the deck. */
  readonly elevation: number;
  /** Edges (outline index) where the railing was switched off by hand. */
  readonly off: readonly number[];
  /** The deck itself, so it is not its own neighbour (and its own office walls do not count). */
  readonly self: { readonly mezzanineId?: string; readonly officeId?: string };
}

/** Walkable top of an office built into a hall ("Empore"): outline, height and host hall. */
export function officeDeck(
  doc: Pick<ProjectDocument, 'buildings'>,
  b: Building,
): { polygon: Vec2[]; elevation: number; hall: Building | undefined } | null {
  const o = b.office;
  if (!o || o.placement !== 'indoor' || !o.usableTop) return null;
  return {
    polygon: officeSlabOutline(b),
    elevation: levelElevations(o)[o.floors.length] ?? b.eaveHeight,
    hall: hostHall(doc, b) ?? undefined,
  };
}

/** Railing of an office deck: free edges only (not along the hall wall), gaps at its stairs. */
export function officeDeckRailings(doc: DeckDoc, b: Building): { a: Vec2; b: Vec2 }[] {
  const deck = officeDeck(doc, b);
  const floors = b.office?.floors.length ?? 0;
  if (!deck) return [];
  const stairs = Object.values(doc.stairs).filter(
    (s) => s.buildingId === b.id && s.fromLevel + 1 === floors,
  );
  return deckRailings(doc, deck.hall, deck.polygon, stairs, {
    elevation: deck.elevation,
    off: b.office?.deckRailingOff ?? [],
    self: { officeId: b.id },
  });
}

/** Column positions: free corners plus intermediate supports along long free edges. */
export function mezzanineColumns(doc: Pick<ProjectDocument, 'buildings'>, m: Mezzanine): Vec2[] {
  if (!m.columns) return [];
  const edges = mezzanineEdges(doc.buildings[m.buildingId], m);
  const inset = insetPolygon(m.polygon, MEZZANINE_COLUMN / 2 + 0.05);
  if (inset.length !== m.polygon.length) return [];
  const n = inset.length;
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const before = edges[(i - 1 + n) % n];
    const after = edges[i];
    if (before?.free || after?.free) out.push(inset[i] as Vec2);
    if (!after?.free) continue;
    const { a, b } = edgeOf(inset, i);
    const k = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / COLUMN_SPACING);
    for (let j = 1; j < k; j++)
      out.push({ x: a.x + ((b.x - a.x) * j) / k, y: a.y + ((b.y - a.y) * j) / k });
  }
  return out;
}

/**
 * Where a new straight stair goes: in front of the longest free edge it fits in front of
 * (inside the hall), arriving in the middle of that edge. Without such an edge the longest
 * free edge is used; null when the mezzanine has no free edge at all.
 */
export function mezzanineStairSpot(
  doc: Pick<ProjectDocument, 'buildings'>,
  m: Mezzanine,
  width: number,
): { center: Vec2; rotation: number } | null {
  return deckStairSpot(doc.buildings[m.buildingId], m.polygon, m.elevation, width);
}

/** Stair spot in front of a free edge of any walkable deck climbing `rise` (see above). */
export function deckStairSpot(
  hall: Building | undefined,
  polygon: readonly Vec2[],
  rise: number,
  width: number,
): { center: Vec2; rotation: number } | null {
  const length = (e: MezzanineEdge) => Math.hypot(e.b.x - e.a.x, e.b.y - e.a.y);
  const free = mezzanineEdges(hall, { polygon: [...polygon] })
    .filter((e) => e.free && length(e) >= width)
    .sort((p, q) => length(q) - length(p));
  const depth = stairLayout('straight', width, 'right', rise).depth;
  const inner = hall ? hallInterior(hall) : [];
  const spots = free.map((e) => {
    const len = length(e);
    const dir = { x: (e.b.x - e.a.x) / len, y: (e.b.y - e.a.y) / len };
    // Outward normal of a counter-clockwise outline.
    const n = { x: dir.y, y: -dir.x };
    const c = mid(e.a, e.b);
    const corners = [-width / 2, width / 2].flatMap((s) =>
      [0, depth].map((d) => ({ x: c.x + dir.x * s + n.x * d, y: c.y + dir.y * s + n.y * d })),
    );
    return {
      fits: corners.every((p) => pointInOrOnPolygon(p, inner, 0.01)),
      spot: {
        center: { x: c.x + (n.x * depth) / 2, y: c.y + (n.y * depth) / 2 },
        // The stair walks along its local +y, which has to point back onto the mezzanine.
        rotation: Math.atan2(n.x, -n.y),
      },
    };
  });
  return (spots.find((s) => s.fits) ?? spots[0])?.spot ?? null;
}
